import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import _ from 'underscore';
import sources from 'helpers/sources';
import Button from '../../components/button';
import Checkbox from '../../components/checkbox';
import { Gym } from '../../types/gym';
import { Invoice } from '../../types/invoice';
import notification from 'helpers/notification';
import api from 'helpers/api';
import currencyFormatter from 'helpers/currency_formatter';
import dateFormatter from 'helpers/date_formatter';
import replaceItem from 'helpers/replace_item';
import { useAngularService } from 'helpers/get_angular_service';
import DateInput from '../../components/date_input';
import Table from '../../components/table';
import { useExtendState } from 'helpers/use_extend_state';
import ContactLink from '../../components/contact_link';
import Input from '../../components/input';
import contactType from 'helpers/contact_type';
import toFixed from 'helpers/to_fixed';
import ErrorMessage from '../../components/error_message';

type SelectedInvoice = Invoice & { applyAmount?: number };

const ApplyVoucherModal = ({ modalInstance, voucher }) => {
  const $rootScope = useAngularService('$rootScope');
  const gym: Gym = $rootScope.gym;
  const [step, setStep] = useState(1);

  const [isLoading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [selectedInvoices, setSelectedInvoices] = useState<SelectedInvoice[]>([]);
  const [generalError, setGeneralError] = useState('');

  const [filters, extendFilters] = useExtendState<any>({
    due_date_after: gym.today,
    due_date_before: moment().add(1, 'month').format('YYYY-MM-DD'),
    submitted: false,
    pending: false,
    paid: false,
    failed: false,
    chargeback: false,
    skipped: false,
    process_manually: false,
    auto_reschedule: false,
    upcoming: true,
  });

  useEffect(() => {
    setSelectedInvoices([]);
  }, [filters]);

  const cancel = () => {
    modalInstance.dismiss('cancel');
  };

  const getInvoices = (page) => {
    setLoading(true);
    const url = sources.invoices(gym.id);

    api
      .get(url, {
        page,
        count: 20,
        contact_id: voucher.contact_id,
        family: voucher.share_with_family ? true : undefined,
        voucher: voucher.share_with_family ? true : undefined,
        filters,
      })
      .then(
        (data) => {
          setInvoices(data);
          setLoading(false);
        },
        () => {
          notification.error('Request could not be completed. Please try again');
          setLoading(false);
        },
      );
  };

  const isInvoiceSelected = (invoice) => {
    return _.find(selectedInvoices, (item) => item.id === invoice.id);
  };

  const invoicesColumns = useMemo(
    () =>
      _.compact([
        {
          Header: '',
          id: 'selec',
          Cell: ({ row }) => (
            <Checkbox
              variant="v2"
              checked={isInvoiceSelected(row.original)}
              onChange={(checked) => {
                if (checked) {
                  setSelectedInvoices((items) => [...items, row.original]);
                } else {
                  setSelectedInvoices((items) =>
                    _.filter(items, (item) => item.id !== row.original.id),
                  );
                }
              }}
            />
          ),
        },
        {
          Header: 'Invoice Number',
          accessor: 'invoice_number_base',
        },
        voucher.share_with_family
          ? {
              Header: 'Contact',
              id: 'contact',
              Cell: ({ row }) => (
                <ContactLink
                  id={row.original.contact_id}
                  name={row.original.contact_name}
                  type={row.original.contact_type}
                  status={row.original.contact_status}
                  archived={row.original.contact_archived}
                  target="_blank"
                />
              ),
            }
          : null,
        {
          Header: 'Membership',
          accessor: 'membership_label',
        },
        {
          Header: 'Amount',
          id: 'amount',
          Cell: ({ row }) => (
            <div>
              <div>{currencyFormatter(row.original.amount, gym.currency_symbol)}</div>
              {+row.original.credit_applied_amount > 0 && (
                <small>
                  {currencyFormatter(row.original.credit_applied_amount, gym.currency_symbol)}{' '}
                  credit applied
                </small>
              )}
            </div>
          ),
        },
        {
          Header: 'Due',
          id: 'due',
          Cell: ({ row }) => dateFormatter(row.original.due_date),
        },
      ]),
    [selectedInvoices],
  );

  const isFirstStepReady = () => {
    return selectedInvoices.length;
  };

  const isSecondStepReady = () => {
    const totalAmount = getTotalAmount();

    return (
      !isLoading &&
      selectedInvoices.length &&
      totalAmount > 0 &&
      totalAmount <= +voucher.current_balance &&
      !_.find(selectedInvoices, getInvoiceValidationError)
    );
  };

  const getTotalAmount = () => {
    return selectedInvoices.reduce((prev, item) => {
      return toFixed(prev + (+item.applyAmount || 0));
    }, 0);
  };

  const getGeneralError = () => {
    const totalAmount = getTotalAmount();

    if (totalAmount > +voucher.current_balance) {
      return "Total credit applied exceeds the voucher's balance";
    }

    return generalError;
  };

  const getInvoiceValidationError = (invoice: SelectedInvoice) => {
    const remainingAmount = toFixed(+invoice.amount - (+invoice.credit_applied_amount || 0));
    if (invoice.applyAmount > remainingAmount) {
      return 'Credit to apply is too large: please specify a smaller amount';
    }

    if (remainingAmount - invoice.applyAmount < 1 && remainingAmount - invoice.applyAmount > 0) {
      return 'Credit to apply is invalid: Invoice balance after credit must be 1 or higher';
    }
  };

  const updateSelectedInvoice = (invoice, index) => {
    setSelectedInvoices((items) => {
      return replaceItem(items, invoice, index);
    });
  };

  const applyCredit = () => {
    setLoading(true);

    const vouchersUrl = sources.contactCreditVouchers({
      gymId: gym.id,
      contactId: voucher.contact_id,
      contactType: contactType(voucher.contact_type),
    });
    const url = `${vouchersUrl}/${voucher.id}/apply_credit`;

    const params = {
      credit_voucher_activity: {
        redemption_targets: selectedInvoices
          .filter((item) => item.applyAmount > 0)
          .map((item) => ({
            id: item.id,
            type: 'Invoice',
            amount: item.applyAmount,
          })),
      },
    };

    setGeneralError('');

    api.put(url, params).then(
      () => {
        modalInstance.close();
        notification.success('Credit applied successfully');
      },
      (response) => {
        setLoading(false);
        notification.error('Please fix the errors below');

        if (response.data.errors.general) {
          setGeneralError(response.data.errors.general);
        }
      },
    );
  };

  return (
    <div>
      <div className="modal-header">
        <a aria-hidden="true" className="close-modal" onClick={cancel} title="Close">
          <i className="icon-cross"></i>
        </a>
        <h4 className="modal-title">Apply credit to payments</h4>
      </div>
      {step === 1 ? (
        <>
          <div className="modal-body modal-body-visible">
            <div className="date-range date-range--v2 margin-bottom-16">
              <div className="date-from">
                <DateInput
                  value={filters.due_date_after}
                  variant="36"
                  onChange={(due_date_after) => extendFilters({ due_date_after })}
                  minDate={gym.today}
                  hideClearDate
                />
              </div>
              <div className="date-to">
                <DateInput
                  value={filters.due_date_before}
                  variant="36"
                  onChange={(due_date_before) => extendFilters({ due_date_before })}
                />
              </div>
            </div>

            <div className="margin-bottom-16">
              Showing upcoming payments for{' '}
              <strong>
                {voucher.contact_name} {voucher.share_with_family ? 'and family' : ''}
              </strong>
            </div>
            <Table
              columns={invoicesColumns}
              data={invoices}
              loading={isLoading}
              filters={filters}
              emptyText="No results"
              pageSize={25}
              loadData={(page) => getInvoices(page)}
              variant="v2"
            />
          </div>
          <div className="modal-footer">
            <Button onClick={cancel}>Cancel</Button>
            <Button variant="success" onClick={() => setStep(2)} disabled={!isFirstStepReady()}>
              Next: Confirm amounts
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="modal-body modal-body-visible">
            <div>Credit voucher:{voucher.name_for_display}</div>
            <div>
              Current balance:{currencyFormatter(voucher.current_balance, gym.currency_symbol)}
            </div>
            <div className="margin-bottom-16">
              <strong>Update the list below to apply credit to the selected payments:</strong>
            </div>

            <ErrorMessage error={getGeneralError()}></ErrorMessage>

            <table className="table worx-table worx-table--v2">
              <thead>
                <tr>
                  <th>Invoice number</th>
                  {voucher.share_with_family ? <th>Contact</th> : null}
                  <th>Membership</th>
                  <th>Amount</th>
                  <th>Credit to apply</th>
                  <th>Due</th>
                </tr>
              </thead>
              <tbody>
                {selectedInvoices.map((invoice, index) => (
                  <tr key={invoice.id}>
                    <td>{invoice.invoice_number_base}</td>
                    {voucher.share_with_family ? (
                      <td>
                        <ContactLink
                          id={invoice.contact_id}
                          name={invoice.contact_name}
                          type={invoice.contact_type}
                          status={invoice.contact_status}
                          archived={invoice.contact_archived}
                          target="_blank"
                        />
                      </td>
                    ) : null}

                    <td>{invoice.membership_label}</td>
                    <td>
                      <div>{currencyFormatter(invoice.amount, gym.currency_symbol)}</div>
                      {+invoice.credit_applied_amount > 0 && (
                        <small>
                          {currencyFormatter(invoice.credit_applied_amount, gym.currency_symbol)}{' '}
                          credit applied
                        </small>
                      )}
                    </td>
                    <td>
                      <Input
                        size="sm"
                        type="number"
                        value={invoice.applyAmount}
                        onChange={(applyAmount) =>
                          updateSelectedInvoice({ ...invoice, applyAmount }, index)
                        }
                        min={0}
                      />

                      <ErrorMessage error={getInvoiceValidationError(invoice)}></ErrorMessage>
                    </td>
                    <td>{dateFormatter(invoice.due_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="modal-footer">
            <Button onClick={cancel}>Cancel</Button>
            <Button variant="success" onClick={() => applyCredit()} disabled={!isSecondStepReady()}>
              Apply credit now
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

angular.module('gym').value('ApplyVoucherModal', ApplyVoucherModal);
